import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { 
  Edit2, Trash2, MoreVertical, Clock, Globe, Lock, Image, Users, Download, Share2, LogOut 
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Cover image gradients/patterns
export const COVER_IMAGES = {
  'gradient-purple': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'gradient-blue': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'gradient-green': 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  'gradient-orange': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'gradient-pink': 'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)',
  'pattern-runes': 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  'pattern-glyphs': 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
  'pattern-waves': 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
  'abstract-language': 'linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)',
  'abstract-scripts': 'linear-gradient(135deg, #e65c00 0%, #f9d423 100%)',
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getVisibilityInfo = (conlang, isSharedTab) => {
  if (isSharedTab) {
    return { icon: Users, label: 'Shared', color: 'bg-secondary/80' };
  }
  if (conlang.visibility === 'public') {
    return { icon: Globe, label: 'Public', color: 'bg-green-500/80' };
  }
  if (conlang.shared_with && conlang.shared_with.length > 0) {
    return { icon: Users, label: 'Shared', color: 'bg-blue-500/80' };
  }
  return { icon: Lock, label: 'Private', color: 'bg-gray-500/80' };
};

export const LanguageCard = ({ 
  conlang, 
  isSharedTab = false,
  onEdit,
  onDelete,
  onChangeCover,
  onToggleVisibility,
  onExport,
  onShare,
  onLeave
}) => {
  const navigate = useNavigate();
  
  const isCustomImage = conlang.cover_image?.startsWith('data:image');
  const coverStyle = isCustomImage 
    ? { backgroundImage: `url(${conlang.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: COVER_IMAGES[conlang.cover_image] || COVER_IMAGES['gradient-purple'] };

  const visInfo = getVisibilityInfo(conlang, isSharedTab);
  const IconComponent = visInfo.icon;

  return (
    <Card 
      className="bg-background-paper border-border hover:border-primary/50 transition-colors duration-300 cursor-pointer group overflow-hidden"
      data-testid={`conlang-card-${conlang.conlang_id}`}
      onClick={() => navigate(`/conlang/${conlang.conlang_id}`)}
    >
      {/* Cover Image */}
      <div className="h-24 relative" style={coverStyle}>
        {/* Visibility Badge */}
        <div className={`absolute top-2 left-2 ${visInfo.color} rounded-full px-2 py-1 flex items-center gap-1`}>
          <IconComponent className="w-3 h-3 text-white" />
          <span className="text-white text-xs">{visInfo.label}</span>
        </div>
        
        {/* Shared tab extras */}
        {isSharedTab && (
          <div className="absolute top-2 right-2 bg-black/50 rounded-full px-2 py-1 flex items-center gap-1">
            <span className="text-white text-xs">by {conlang.owner_name}</span>
          </div>
        )}
        {isSharedTab && conlang.access_level === 'edit' && (
          <div className="absolute bottom-2 right-2 bg-secondary/90 rounded-full px-2 py-1">
            <span className="text-black text-xs font-medium">Co-creator</span>
          </div>
        )}
      </div>
      
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className="font-heading text-xl text-foreground truncate group-hover:text-primary transition-colors">
              {conlang.name}
            </CardTitle>
            {conlang.native_name && (
              <CardDescription className="font-mono text-secondary mt-1">
                {conlang.native_name}
              </CardDescription>
            )}
          </div>
          
          {/* Dropdown menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0" 
                onClick={(e) => e.stopPropagation()} 
                data-testid={`conlang-menu-${conlang.conlang_id}`}
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-background-paper border-border">
              {!isSharedTab && (
                <>
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); onToggleVisibility?.(conlang); }} 
                    className="cursor-pointer"
                    data-testid={`toggle-visibility-${conlang.conlang_id}`}
                  >
                    {conlang.visibility === 'public' ? (
                      <><Lock className="w-4 h-4 mr-2" />Make Private</>
                    ) : (
                      <><Globe className="w-4 h-4 mr-2" />Make Public</>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onChangeCover?.(conlang); }} className="cursor-pointer">
                    <Image className="w-4 h-4 mr-2" />Change Cover
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit?.(conlang); }} className="cursor-pointer" data-testid={`edit-conlang-${conlang.conlang_id}`}>
                    <Edit2 className="w-4 h-4 mr-2" />Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onShare?.(conlang); }} className="cursor-pointer" data-testid={`share-conlang-${conlang.conlang_id}`}>
                    <Share2 className="w-4 h-4 mr-2" />Share
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onExport?.(conlang); }} className="cursor-pointer" data-testid={`export-conlang-${conlang.conlang_id}`}>
                <Download className="w-4 h-4 mr-2" />Export
              </DropdownMenuItem>
              {isSharedTab && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onLeave?.(conlang); }} className="text-destructive cursor-pointer" data-testid={`leave-conlang-${conlang.conlang_id}`}>
                    <LogOut className="w-4 h-4 mr-2" />Leave
                  </DropdownMenuItem>
                </>
              )}
              {!isSharedTab && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete?.(conlang); }} className="text-destructive cursor-pointer" data-testid={`delete-conlang-${conlang.conlang_id}`}>
                    <Trash2 className="w-4 h-4 mr-2" />Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent>
        {conlang.description && (
          <p className="text-foreground-muted text-sm line-clamp-2 mb-3">{conlang.description}</p>
        )}
        <div className="flex items-center gap-2 text-foreground-subtle text-xs">
          <Clock className="w-3 h-3" />
          <span>Created {formatDate(conlang.created_at)}</span>
        </div>
      </CardContent>
    </Card>
  );
};
